import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IUser } from 'app/core/models/auth/auth-contracts.model';
import { AuthService } from 'app/core/services/auth.service';
import { hasCurrentAcceptance } from 'app/features/photoqa/models/subject-consent.model';
import { SubjectConsentsService } from 'app/features/photoqa/services/subject-consents.service';
import { responsiveDialogConfig } from 'app/shared/utils/dialog-config';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { ConsentGateDialogComponent } from './consent-gate-dialog/consent-gate-dialog.component';

/**
 * First-login Terms + AI-processing acceptance gate (WS-H §6.5/§9 MR8,
 * PFL-2569; kanban #3306). Mirrors iam-portal's `ConsentGate`: a signed-in user
 * whose `Terms`/`AiProcessing` consents are missing, withdrawn, or recorded under
 * an older policy version must accept the current text before using the app.
 *
 * Checked once per signed-in user, off `AuthService.user$` (emits on BFF session
 * restore, sign-in, and sign-out → null). Fail-open on read errors: a consent-API
 * outage must not lock users out — the BE stays authoritative.
 */
@Injectable({ providedIn: 'root' })
export class ConsentGateService {
    private readonly _auth = inject(AuthService);
    private readonly _consents = inject(SubjectConsentsService);
    private readonly _dialog = inject(MatDialog);

    private _checkedUserId: string | null = null;
    private _wired = false;

    /** Subscribe once; idempotent. Called from `AppComponent.ngOnInit`. */
    init(): void {
        if (this._wired) return;
        this._wired = true;

        this._auth.user$
            .pipe(
                filter((u): u is IUser => u !== null),
                // Kanban #3779: an anonymous album-grant principal carries a
                // grant_id, not an account — /me/consents 403s for it and the
                // error interceptor toasts on every public-album load.
                filter((u) => !u.grant_id),
                filter((u) => u.user_id !== this._checkedUserId),
                switchMap((u) =>
                    forkJoin({
                        // `true`: background reads — fail-open here, so no error toast.
                        version: this._consents
                            .getConsentPolicyVersion(true)
                            .pipe(catchError(() => of(null))),
                        consents: this._consents
                            .getMyConsents(true)
                            .pipe(catchError(() => of(null))),
                        // Carried through so a fail-open (or already-accepted) outcome still
                        // stamps the check — without it _checkedUserId was only set when the
                        // dialog opened, so every later user$ emission (permissions
                        // hydration, token refresh, BFF reconnect) refetched policy+consents
                        // and burned the user's hourly rate budget (kanban #3778).
                        userId: of(u.user_id),
                    })
                )
            )
            .subscribe(({ version, consents, userId }) => {
                // Checked even on fail-open: a retry wants a fresh user$, not every
                // emission (see above). A transient consent-API outage must not turn
                // each subsequent emission into another API call.
                this._checkedUserId = userId;

                if (!version || !consents) return; // fail-open
                if (hasCurrentAcceptance(consents, version)) return;

                this._dialog.open(
                    ConsentGateDialogComponent,
                    responsiveDialogConfig('450px', {
                        minWidth: '250px',
                        disableClose: true,
                        // #3807: a blocking gate — MatDialog defaults to
                        // aria-modal=false, which lets screen-reader users
                        // wander into the page behind it.
                        ariaModal: true,
                        data: { policyVersion: version },
                    })
                );
            });
    }
}
